import React from "react"
import { fromJS } from "immutable"
import moment from "moment"
import { formValueSelector } from "redux-form/immutable"
import { createSelector, createStructuredSelector } from "reselect"
import {
    pacSelector,
    selectIsLoadingFiling,
    selectIsLoadingDotFecFile,
    selectLedgerAccounts,
    selectLedgerSettingsFilingFrequency,
    selectLedgerSettingsId,
    selectNextFilingCoverageStart,
    selectGeneralElection,
    selectJurisdictions,
} from "shared/pac/selectors"
import { selectLedgerSettingsHomePath } from "pac/selectors/pac-selectors"

import { centsToCurrencyStr } from "shared/pac/helperFunctions"
import {
    getFilingEditPath,
    getCoveragePeriodStrForFilingTable,
    formatDateShort,
} from "app/static/frontend/pac/helperFunctions"

import { getPacPaths } from "imports/desktopPaths"
import { FEC_ID_PREFIX, FILING_CREATE_FORM_KEY } from "shared/pac/constants"

import ActionColumnGrid from "sheets/components/cells/ActionColumnGrid"
import EditPacFileAction from "sheets/components/cells/action-buttons/EditPacFileAction"
import ArchivePacFileAction from "sheets/components/cells/action-buttons/ArchivePacFileAction"
import AmendPacFileAction from "sheets/components/cells/action-buttons/AmendPacFileAction"
import { AbstractEnumSelect } from "selects/containers/EnumSelect"
import constants from "app/static/frontend/sheets/constants"
import { CompletedGreen } from "design-constants"

const { Form3XReportType, PacFileFormType, PacFileFormStatusType, ReportType } = DjangIO.app.pac.types
const { ReportingJurisdictionType } = DjangIO.app.ledger.types
const { Region } = DjangIO.app.models

export const selectFilingsId = createSelector(
    (_, props) => props.params,
    (params) => params && params.filingsId,
)

export const selectFormTypeFromParams = createSelector(
    (_, props) => props.params,
    (params) => params && params.formType && parseInt(params.formType),
)

const actionColumnButtons = [
    {
        name: "Edit",
        icon: "pencil",
        requiredFields: ["id"],
        action: EditPacFileAction,
    },
    {
        name: "Archive",
        icon: "archive",
        requiredFields: ["id"],
        action: ArchivePacFileAction,
    },
    {
        name: "Amend",
        icon: "edit",
        requiredFields: ["id", "ledgerSettingsId"],
        requiredResults: {
            isAmendment: false,
            jurisdictionAllowsAmendments: true,
        },
        action: AmendPacFileAction,
    },
]

export const selectResourceTableColumns = createSelector(
    selectLedgerSettingsId,
    selectJurisdictions,
    (ledgerSettingsId, jurisdictions) => [
        {
            name: "Actions", // Compliance Report Table
            field: [
                "id",
                "form_type",
                "status",
                "is_editable",
                "is_amendable",
                "_extra",
                "amendment_form_id",
                "region",
                "index",
            ],
            display: (
                filingsId,
                formType,
                formStatus,
                isEditable,
                isAmendable,
                extra,
                amendmentFormId,
                regionId,
                index,
            ) => {
                return (
                    <ActionColumnGrid
                        buttons={actionColumnButtons}
                        format={constants.ACTION_COLUMN_GRID_FORMAT.DEFAULT}
                        fields={{
                            id: filingsId,
                            ledgerSettingsId,
                            isEditable,
                            isAmendable,
                            formType,
                            regionId,
                            // used by <EditPacFileAction>
                            editPath: getFilingEditPath(ledgerSettingsId, filingsId, formType, regionId),
                            // We use index here to get the first entry on the sheet, which will be the latest filing submitted.
                            isArchivable: extra.get("is_archivable") || false,
                            // If amendmentFormId is present, it means this PacFile is an amendment.
                            // Only show the "Amend" button for original filings, and not for amendments.
                            isAmendment: !!amendmentFormId,
                            jurisdictionAllowsAmendments: ReportingJurisdictionType.by_value(regionId).is_amendable,
                            isFiled: formStatus === PacFileFormStatusType.filed.value,
                        }}
                    />
                )
            },
            width: 290,
        },
        {
            name: "Coverage Dates",
            field: ["start_date", "end_date"],
            display: (startDate, endDate) => getCoveragePeriodStrForFilingTable(startDate, endDate),
            resourceTableEnableFiltering: true,
            resourceTableEnableSorting: true,
            resourceTableFilterOn: "start_date",
            resourceTableSortOn: "start_date",
            width: 270,
        },
        ...(jurisdictions && jurisdictions.length
            ? [
                  {
                      name: "Jurisdiction",
                      field: "jurisdiction",
                      resourceTableEnableFiltering: true,
                      resourceTableEnableSorting: true,
                      width: 150,
                      enumFilter: (jurisdictionEnum) => {
                          if (jurisdictionEnum.value === DjangIO.app.models.SupportedRegion.federal.value) {
                              return true
                          }

                          return (
                              jurisdictions.length &&
                              jurisdictions.some(
                                  (jurisdiction) => jurisdiction.jurisdiction_type === jurisdictionEnum.value,
                              ) &&
                              (!ReportingJurisdictionType.by_value(jurisdictionEnum.value).disabled ||
                                  jurisdictionEnum.value === ReportingJurisdictionType.federal.value)
                          )
                      },
                  },
              ]
            : []),
        {
            name: "Filing Status",
            field: "status",
            resourceTableEnableFiltering: true,
            resourceTableEnableSorting: true,
            width: 140,
        },
        {
            name: "Filing ID",
            field: "fec_id",
            resourceTableEnableFiltering: true,
            resourceTableEnableSorting: true,
            width: 200,
            display: (value) => (value ? `${FEC_ID_PREFIX}${value}` : ""),
        },
        {
            name: "Date Filed",
            field: "submitted_timestamp",
            resourceTableEnableFiltering: true,
            resourceTableEnableSorting: true,
            width: 200,
            display: (dateFiled) => dateFiled && moment(dateFiled).format("MM/DD/YYYY HH:mm"),
        },
        {
            name: "Report Type",
            field: "pac_report_super_type",
            resourceTableEnableFiltering: true,
            resourceTableFilterOn: "pac_report_super_type_is",
            display: (reportType) => DjangIO.app.pac.types.PacReportSuperType.by_value(reportType).label,
            resourceTableSearchTrigger: ({ value, search }) => (
                <AbstractEnumSelect
                    value={value}
                    options={DjangIO.app.pac.types.PacReportSuperType}
                    onChange={(item) => search(item.value)}
                />
            ),
            width: 200,
        },
        {
            name: "Error Message",
            field: ["latest_submission_error_message"],
            width: 170,
        },
    ],
)

export const filingsResourceTableConnection = createStructuredSelector({
    columns: selectResourceTableColumns,
    ledgerSettingsId: selectLedgerSettingsId,
    filingFrequency: selectLedgerSettingsFilingFrequency,
    sheetId: selectLedgerSettingsId,
    ledgerSettingsHomePath: selectLedgerSettingsHomePath,
    generalElection: selectGeneralElection,
    jurisdictions: selectJurisdictions,
})

const makeFilingCreateFormValueSelector = (field) => (state) => formValueSelector(FILING_CREATE_FORM_KEY)(state, field)

const selectLatestFiling = createSelector(
    (state) => state.pac,
    (pac) => pac.get("latestFiling"),
)

const selectLatestFilingEndDatePlusOne = createSelector(selectLatestFiling, (latestFiling) => {
    const previousEndDate = latestFiling && latestFiling.get("coverage_end_date")
    return previousEndDate && moment(previousEndDate).add(1, "days").format("MM/DD/YYYY")
})

export const selectFilingCreateFormInitialValues = createSelector(
    selectLatestFilingEndDatePlusOne,
    (previousEndDatePlusOne) => ({
        form_type: PacFileFormType.form_f3x.value,
        report_type: ReportType.balance_sheet.value,
        specialFilingCoverageStart: previousEndDatePlusOne,
        specialFilingCoverageEnd: moment().format("MM/DD/YYYY"),
    }),
)

export const selectFilingDeadlines = createSelector(
    selectLedgerSettingsFilingFrequency,
    makeFilingCreateFormValueSelector("report_type"),
    (filingFrequency, selectedReportType) =>
        Form3XReportType.items()
            .filter(
                (reportType) =>
                    reportType.form_period_type === filingFrequency || reportType.form_period_type === undefined,
            )
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((reportType, idx) => ({
                coverageEndDate: `${reportType.coverage_end_month_day}/${moment().year()}`,
                coverageStartDate: `${reportType.coverage_start_month_day}/${moment().year()}`,
                dataCy: reportType.dataCy,
                icon: "file-invoice-dollar",
                iconFamily: "fas",
                iconSize: "3x",
                id: reportType.value,
                positions: [],
                selected: selectedReportType === reportType.value,
                sort: idx,
                text: reportType.label,
                title: reportType.title,
                widgets: {},
            })),
)

export const selectFilingFormTypes = createSelector(
    makeFilingCreateFormValueSelector("form_type"),
    (selectedFormType) =>
        PacFileFormType.items()
            .filter((item) => !item.disabled)
            .map((item) => ({
                icon: "file-invoice-dollar",
                iconFamily: "fas",
                iconSize: "3x",
                id: item.value,
                order: item.order,
                positions: [],
                selected: selectedFormType === item.value,
                sort: item.order,
                text: item.subtitle,
                title: item.title,
                widgets: {},
            }))
            .sort(DjangIO.orderSort),
)

export const createFilingDeadlineSelector = (fieldName) =>
    createSelector(
        selectFilingDeadlines,
        makeFilingCreateFormValueSelector("report_type"),
        (deadlines, selectedReportType) => {
            const filingDeadline = deadlines.find((deadline) => deadline.id === selectedReportType)
            if (filingDeadline) {
                return filingDeadline[fieldName]
            }
        },
    )

export const selectFilingCreateFormTitle = createSelector(
    (state, props) => props.modalPage,
    (modalPage) => {
        switch (modalPage) {
            default:
                console.error(`Unexpected modal page ${modalPage}`)
                break
            case 1:
                return "Choose Where And What You Want to File"
            case 2:
                return "Choose a Report Type for Federal - Form 3X"
            case 3:
                return "Set Up the Special Report"
        }
    },
)

export const selectShouldShowForm1Form = createSelector(
    (state, props) => props.modalPage,
    makeFilingCreateFormValueSelector("form_type"),
    (modalPage, selectedFormType) => modalPage === 1 && selectedFormType === PacFileFormType.form_f1.value,
)

export const selectIsElectionFilingPeriodSelected = createSelector(
    makeFilingCreateFormValueSelector("report_type"),
    (selectedReportType) =>
        [Form3XReportType.pre_election.value, Form3XReportType.post_election.value].includes(selectedReportType),
)

export const selectIsTerminationFilingSelected = createSelector(
    makeFilingCreateFormValueSelector("report_type"),
    (selectedReportType) => selectedReportType === Form3XReportType.termination_report.value,
)

export const filingCreateFormConnection = createStructuredSelector({
    initialValues: selectFilingCreateFormInitialValues,
    filingDeadlines: selectFilingDeadlines,
    filingFormTypes: selectFilingFormTypes,
    ledgerSettingsId: selectLedgerSettingsId,
    title: selectFilingCreateFormTitle,
    isElectionFilingPeriodSelected: selectIsElectionFilingPeriodSelected,
    isTerminationFilingSelected: selectIsTerminationFilingSelected,

    specialFilingCoverageStart: selectLatestFilingEndDatePlusOne,
    specialFilingCoverageEnd: makeFilingCreateFormValueSelector("specialFilingCoverageEnd"),
    selectedFormType: makeFilingCreateFormValueSelector("form_type"),
    selectedReportType: makeFilingCreateFormValueSelector("report_type"),
    filingFrequency: selectLedgerSettingsFilingFrequency,
    nextFilingCoverageStart: selectNextFilingCoverageStart,
    generalElection: selectGeneralElection,
    shouldShowForm1Form: selectShouldShowForm1Form,
})

// FilingDetailView selectors
export const selectFilingObj = createSelector(pacSelector, (state) => state.get("filing", fromJS({})))

export const createFilingSelector = (fieldName) => createSelector(selectFilingObj, (filing) => filing.get(fieldName))

export const createPercentageStatusSelector = (fieldName) =>
    createSelector(
        createFilingSelector("percentage_status"),
        (percentageStatus) =>
            (percentageStatus && percentageStatus.get(fieldName) && percentageStatus.get(fieldName) / 100) || 0,
    )

export const createFecForm3XSubmissionSelector = (fieldName) =>
    createSelector(createFilingSelector("fec_form_3x_submission"), (fecForm) => fecForm && fecForm.get(fieldName))

export const selectReconcilePercentage = createPercentageStatusSelector("reconcile_percentage")

export const selectErrorsPercentage = createPercentageStatusSelector("go_through_errors_percentage")

export const selectReviewPercentage = createPercentageStatusSelector("review_and_file_percentage")

export const selectIsReconcileCompleted = createSelector(
    selectReconcilePercentage,
    (reconcilePercentage) => reconcilePercentage === 1,
)

export const selectIsGoThroughErrorsCompleted = createSelector(
    selectErrorsPercentage,
    (errorsPercentage) => errorsPercentage === 1,
)

export const selectIsReviewCompleted = createSelector(
    selectReviewPercentage,
    (reviewPercentage) => reviewPercentage === 1,
)

export const createCoverageDateSelector = (fieldName) =>
    createSelector(createFilingSelector(fieldName), (field) => formatDateShort(field, true))

export const selectFormTitle = createSelector(
    createFilingSelector("form_type"),
    createFecForm3XSubmissionSelector("report_code"),
    (formType, reportCode) => {
        let formTitle = ""
        const pacFileFormType = PacFileFormType.by_value(formType)
        if (!pacFileFormType) return formTitle

        const formTypeTitle = pacFileFormType.title
        if (pacFileFormType === PacFileFormType.form_f3x) {
            const formReportType = reportCode && Form3XReportType.by_value(reportCode)
            if (!formReportType) return formTitle

            const formPeriodTitle = formReportType.title
            const formPeriodKey = formReportType.code

            if (
                [
                    Form3XReportType.pre_election,
                    Form3XReportType.post_election,
                    Form3XReportType.termination_report,
                ].includes(formReportType)
            ) {
                formTitle = `${formTypeTitle} - ${formPeriodTitle}`
            } else {
                formTitle = `${formTypeTitle} - ${formPeriodKey} - ${formPeriodTitle}`
            }
        } else {
            formTitle = `${formTypeTitle}`
        }

        return formTitle
    },
)

export const selectFormType = createFilingSelector("form_type")

export const selectFilingSubmitReportName = createSelector(selectFormTypeFromParams, (formType) => {
    const formTypeTitle = PacFileFormType.by_value(formType) && PacFileFormType.by_value(formType).title
    return `FEC ${formTypeTitle} Report`
})

export const selectColABeginningCashOnHand = createSelector(
    createFecForm3XSubmissionSelector("col_a_cash_on_hand_beginning"),
    centsToCurrencyStr,
)

export const selectColBBeginningCashOnHand = createSelector(
    createFecForm3XSubmissionSelector("col_b_cash_on_hand_beginning"),
    centsToCurrencyStr,
)

export const selectIsJanFirstStartDate = createSelector(
    createCoverageDateSelector("coverage_start_date"),
    (coverageStartDate) => {
        if (coverageStartDate) {
            const [month, day] = coverageStartDate.split("/")
            if (month === "1" && day === "1") {
                return true
            }
        }
        return false
    },
)

export const selectAmendmentNumber = createFilingSelector("amendment_number")

const selectAmendments = createSelector(createFilingSelector("amendments"), (amendments) => amendments?.toJSON())

export const selectFilingStatus = createSelector(createFilingSelector("status"), (status) =>
    status === undefined ? undefined : PacFileFormStatusType.by_value(status),
)

/**
 * Get objects representing accounts to be reconciled with information about the reconciliation status and how
 * how to display it.
 *
 * @returns {array}
 */
export const selectAccountsToReconcile = createSelector(
    selectLedgerAccounts,
    selectLedgerSettingsId,
    (state, props) => state.pac.getIn(["filing", "percentage_status"]),
    (ledgerAccounts, ledgerSettingsId, percentageStatus) => {
        if (!ledgerAccounts || !percentageStatus) {
            return []
        }

        const reconcileData = percentageStatus.get("reconcile_data")
        const accountsIdsToReconcile = Array.from(reconcileData.keys()).map((id) => parseInt(id))
        const relevantAccounts = ledgerAccounts.filter((act) => accountsIdsToReconcile.includes(act.get("id")))

        return relevantAccounts
            .map((act) => {
                const reconcile = reconcileData.get(act.get("id").toString())
                const basicAccountInfo = {
                    id: act.get("id"),
                    header: act.get("nickname") || act.get("bank_name") || "Bank Account",
                    accountNumber: act.get("account_number", undefined),
                    icon: "piggy-bank",
                    iconFamily: "fad",
                }

                if (reconcile.get("reconciled")) {
                    // If the account is fully reconciled for the PacFile period, the box should link to the list of
                    // reconciliations for that account
                    return {
                        ...basicAccountInfo,
                        segueLink: getPacPaths({
                            ledgerSettingsId,
                            accountId: act.get("id"),
                        })._pacAccountReconciliationList,
                        complete: true,
                        callToActionText: "View Reconciliations",
                        icon: "check-circle",
                        iconColor: CompletedGreen,
                    }
                } else if (reconcile.get("earliest_incomplete_reconciliation")) {
                    // Otherwise, if we're not fully reconciled but there are is an incomplete reconciliation for
                    // the period, link to it
                    return {
                        ...basicAccountInfo,
                        segueLink: getPacPaths({
                            ledgerSettingsId,
                            reconciliationId: reconcile.getIn(["earliest_incomplete_reconciliation", "id"]),
                        })._pacReconciliationDetail,
                        complete: false,
                        callToActionText: "Continue Reconciliation",
                    }
                } else {
                    // Otherwise, they haven't started a reconciliation so link them to create a new one
                    return {
                        ...basicAccountInfo,
                        segueLink: getPacPaths({
                            ledgerSettingsId,
                            accountId: act.get("id"),
                        })._pacReconciliationNew,
                        complete: false,
                        callToActionText: "Reconcile",
                    }
                }
            })
            .toArray()
    },
)

/**
 * Selects if the filing form is in the process of submitting
 */
export const selectIsFilingFormSubmitting = createSelector(
    (state) => state.form,
    (form) => Boolean(form.getIn(["filings-detail-view", "submitting"])),
)

export const selectCheckErrorsSegueLink = createSelector(
    selectLedgerSettingsId,
    (ledgerSettingsId) => getPacPaths({ ledgerSettingsId })._pacAccountErrorList,
)

export const filingDetailViewConnection = createStructuredSelector({
    //TODO: alphabetize
    coverageEndDate: createCoverageDateSelector("coverage_end_date"),
    coverageStartDate: createCoverageDateSelector("coverage_start_date"),
    filingsId: selectFilingsId,
    pacFile: selectFilingObj,
    formTitle: selectFormTitle,
    ledgerSettingsId: selectLedgerSettingsId,
    loading: selectIsLoadingFiling,
    accountsToReconcile: selectAccountsToReconcile,
    checkErrorsSegueLink: selectCheckErrorsSegueLink,
    reconcilePercentage: selectReconcilePercentage,
    errorsPercentage: selectErrorsPercentage,
    reviewPercentage: selectReviewPercentage,
    isAmendable: createFilingSelector("is_amendable"),
    isEditable: createFilingSelector("is_editable"),
    isReconcileCompleted: selectIsReconcileCompleted,
    isGoThroughErrorsCompleted: selectIsGoThroughErrorsCompleted,
    isReviewCompleted: selectIsReviewCompleted,
    colABeginningCashOnHand: selectColABeginningCashOnHand,
    colBBeginningCashOnHand: selectColBBeginningCashOnHand,
    electionDate: createFecForm3XSubmissionSelector("election_date"),
    electionState: createFecForm3XSubmissionSelector("election_region"),
    electionType: createFecForm3XSubmissionSelector("election_type"),
    form3xId: createFecForm3XSubmissionSelector("id"),
    isJanFirstStartDate: selectIsJanFirstStartDate,
    formType: selectFormType,
    reportType: createFecForm3XSubmissionSelector("report_code"),
    fecId: createFilingSelector("fec_id"),
    filingDate: createFilingSelector("submitted_timestamp"),
    filingStatus: selectFilingStatus,
    amendmentNumber: selectAmendmentNumber,
    amendments: selectAmendments,
    amendsFilingId: createFilingSelector("amends_filing_id"),
    amendmentFormId: createFilingSelector("amendment_form_id"),
    amendsTimestamp: createFilingSelector("amends_timestamp"),
    description: createFilingSelector("description"),
})

export const filingSubmitPageConnection = createStructuredSelector({
    filingsId: selectFilingsId,
    ledgerSettingsId: selectLedgerSettingsId,
    loadingDotFecFile: selectIsLoadingDotFecFile,
    formType: selectFormTypeFromParams,
    reportName: selectFilingSubmitReportName,
    fecId: createFilingSelector("fec_id"),
    hasCriticalErrors: createFilingSelector("is_errors_found"),
})
